import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Check, Crown, ArrowLeft, Loader2, Info } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import MainMenu from '@/components/MainMenu';
import SeoHead from '@/components/SeoHead';

export default function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: "free",
      name: "Free",
      price: "$0",
      period: "",
      priceNote: "",
      description: "Perfect for getting started",
      features: [
        "2 resume analyses per month",
        "1 resume vs job comparison per month",
        "1 custom tailored resume generation per month",
        "Resume score out of 100",
        "Improvement feedback"
      ],
      cta: user?.plan === 'free' ? 'Current Plan' : 'Get Started',
      popular: false,
      disabled: user?.plan === 'free'
    },
    {
      id: "pro",
      name: "Pro",
      price: "$4.99",
      period: "/month",
      priceNote: "",
      description: "For serious job seekers",
      features: [
        "Unlimited resume analyses",
        "Unlimited resume vs job description comparisons",
        "1 custom tailored resume generation per month",
        "Keyword matching analysis",
        "Missing skills identification",
        "ATS compatibility score"
      ],
      cta: user?.plan === 'pro' ? 'Current Plan' : 'Upgrade to Pro',
      popular: true,
      disabled: user?.plan === 'pro' || user?.plan === 'premium'
    },
    {
      id: "premium",
      name: "Premium",
      price: "$9.99",
      period: "/month",
      priceNote: "",
      description: "The complete package",
      features: [
        "Everything in Pro",
        "AI resume builder",
        "Resume bullet rewriting",
        "Custom job-tailored cover letter generation",
        "Unlimited custom tailored resume generation",
        "Export as PDF/DOCX",
        "Powered by Claude AI"
      ],
      cta: user?.plan === 'premium' ? 'Current Plan' : 'Go Premium',
      popular: false,
      disabled: user?.plan === 'premium'
    }
  ];

  const handleSelectPlan = async (planId) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (planId === 'free') {
      navigate('/dashboard');
      return;
    }

    setLoading(planId);
    try {
      const data = await api.createCheckout(planId);
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <SeoHead
        title="Pricing | JOBFITAI"
        description="Explore JOBFITAI pricing, including free monthly resume tools, Pro and Premium plans, and paid features for deeper resume tailoring and exports."
        canonicalPath="/pricing"
        robots="index,follow"
      />
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <MainMenu />
            <button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <AppLogo />
          </div>
          {!isAuthenticated && (
            <button
              onClick={() => navigate('/login')}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="login-btn"
            >
              Log in
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Crown className="w-4 h-4" />
            Choose Your Plan
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your job search needs. Upgrade or downgrade anytime.
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative bg-white border rounded-xl overflow-hidden ${
                plan.popular 
                  ? 'border-primary shadow-medium ring-2 ring-primary/20' 
                  : 'border-border shadow-soft'
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="bg-primary text-white text-center py-2 text-sm font-medium">
                  Most Popular
                </div>
              )}
              <div className="p-6">
                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl font-semibold mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-4xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  {plan.priceNote && (
                    <p className="text-xs text-muted-foreground mt-1">{plan.priceNote}</p>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={plan.disabled || loading === plan.id}
                  className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    plan.disabled
                      ? 'bg-surface text-muted-foreground cursor-not-allowed'
                      : plan.popular
                      ? 'bg-primary text-white hover:bg-primary-hover'
                      : 'bg-surface text-foreground hover:bg-surface-highlight border border-border'
                  }`}
                  data-testid={`plan-${plan.id}-cta`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    plan.cta
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

                {/* Recurring Billing Disclosure */}
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="flex items-start gap-3 p-4 bg-surface border border-border rounded-xl text-sm">
            <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground mb-2">Recurring Billing Disclosure</p>
              <p>
                By subscribing to a paid JOBFITAI plan, you agree to recurring charges at the price and billing
                interval shown at checkout. Your subscription will automatically renew at the end of each billing
                period unless you cancel before renewal. You can cancel anytime from your account settings.
              </p>
              <p className="mt-2">
                If you cancel, your plan will remain active until the end of your current paid billing period unless
                otherwise stated. Payments are non-refundable except where required by law. By subscribing, you agree
                to our{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>.
                </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-semibold text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "What's included in the Free plan?",
                a: "The Free plan includes 2 resume analyses, 1 resume-to-job-description comparison, and 1 custom tailored resume generation per month."
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes, you can cancel your subscription at any time."
              },
              {
                q: "How does the resume analysis work?",
                a: "Our AI analyzes your resume against best practices and job requirements, providing a score out of 100 along with detailed feedback."
              },
              {
                q: "What's the difference between Pro and Premium?",
                a: "Pro includes unlimited analyses and comparisons plus 1 custom tailored resume generation per month. Premium includes everything in Pro plus unlimited custom tailored resume generation and PDF/DOCX export."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid={`faq-${i}`}>
                <h3 className="font-heading font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
